import { TranslationKey } from '../../../types/translation/TranslationKey';
import { useMutation } from '@apollo/client';
import React, { useContext, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { CompanyContext } from '../../../context/CompanyContext';
import { CompanyRoleAssignmentContext } from '../../../context/CompanyRoleAssignmentContext';
import { UserContext } from '../../../context/UserContext';
import {
  CompanyMutations,
  CreateDemoCompanyResponse,
  CreateDemoCompanyVariables,
} from '../../../graphql/company.graphql';
import { t } from '../../../types/translation/Translator';
import { OnboardingLS } from '../../../util/local_storage/onboarding.ls';
import { toFilterString } from '../../../util/string.util';
import { Button } from '../../components/Button';
import { ButtonVariant } from '../../components/Button/Button';
import { TextInput } from '../../components/TextInput';
import Icon, { IconType } from '../../icons/Icon';
import { OnboardingRoutes, OnboardingScreen } from '../Onboarding';
import CompanyItem from './common/CompanyItem';

export default function CompaniesScreen() {
  const navigate = useNavigate();
  const { companies } = useContext(CompanyContext);

  const { companyRoles } = useContext(CompanyRoleAssignmentContext);
  const { currentUser } = useContext(UserContext);

  if (!currentUser) return <Navigate to={'/login'} />;

  const [search, setSearch] = useState('');
  const filteredCompanies = useMemo(() => {
    const cleanedSearch = toFilterString(search);

    return [...companies.values()].filter(
      c =>
        toFilterString(c.name).includes(cleanedSearch) &&
        companyRoles.get(currentUser.id)?.find(ca => ca.companyId === c.id)?.role,
    );
  }, [search, companies]);

  const currentUserRoles = companyRoles.get(currentUser.id);

  const [createDemo, { loading }] = useMutation<CreateDemoCompanyResponse, CreateDemoCompanyVariables>(
    CompanyMutations.createDemo,
    {
      onCompleted: () => {
        OnboardingLS.setOnboardedState('DEMO');
        navigate('/dashboard');
        window.location.reload();
      },
    },
  );

  return (
    <div className='flex flex-grow flex-col overflow-auto'>
      <Routes>
        <Route path='onboarding/*' element={<OnboardingScreen />} />

        <Route
          path={'/*'}
          element={(() => {
            if (!companies.size || !currentUserRoles?.length) {
              return (
                <div className='grid flex-grow place-items-center'>
                  <div className='grid w-[635px] place-items-center gap-4 text-center'>
                    <Icon
                      type={IconType.building7}
                      size={'h-24 w-24'}
                      stroke={'stroke-black'}
                      strokeWidth={'stroke-1'}
                    />
                    <div className='grid gap-2'>
                      <p className='text-xl font-semibold text-black'>Start by adding a company</p>
                      <p className='text-sm text-gray-600'>
                        You're not part of a company yet. You can create a new company or join one by invitation. If you
                        expect to be already part of a company, ask your administrator to invite you.
                      </p>
                    </div>
                    <div className='flex gap-2'>
                      <Button
                        text={'Create a company'}
                        onClick={() => navigate(OnboardingRoutes.company)}
                        disabled={loading}
                      />
                      <Button
                        variant={ButtonVariant.secondary}
                        text={'Try a demo company!'}
                        onClick={() => createDemo()}
                        loading={loading}
                      />
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div className='grid gap-8 px-16 py-8 text-black'>
                <div className='flex w-full items-center justify-between'>
                  <p className='text-3xl font-bold'>{t(TranslationKey.company).plural.upper}</p>
                  <Button text={'Create a company'} onClick={() => navigate(OnboardingRoutes.company)} />
                </div>

                <TextInput
                  leading={<Icon type={IconType.searchLg} />}
                  placeholder={'Search by company name'}
                  onChange={value => setSearch(value)}
                  value={search}
                  className='w-[400px]'
                />

                <div className='grid gap-4'>
                  <div>
                    <p className='text-xl font-bold'>Active organizations</p>
                    <p className='text-sm text-gray-600'>{`${filteredCompanies.length} out of ${companies.size} companies`}</p>
                  </div>
                  <div className='flex flex-wrap gap-6'>
                    {filteredCompanies.map(company => {
                      const as = currentUserRoles.find(cr => cr.companyId === company.id);
                      return <CompanyItem key={company.id} company={company} assignment={as} />;
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
        />
      </Routes>
    </div>
  );
}
